<template>
    <div class="payable-receivable-row" :class="rowClass">

        <div class="payable-receivable-row-left">
            <div class="payable-receivable-row-checkbox">
                <b-form-checkbox v-model="selectedIds" :value="item.id" />

            </div>

            <div class="payable-receivable-row-icon" :style="{ background: item.colorIcon }">
                <i :class="item.icon"></i>
            </div>
            <div class="payable-receivable-row-badge-wrap">
                <Badge :title="labelType" :type="item.type" size="small" />
            </div>

            <div class="payable-receivable-row-info">
                <Tooltip class="payable-receivable-row-tooltip" :title="item.recipientName">
                    <div class="payable-receivable-row-recipient" @click="$emit('edit', item)">
                        {{ item.recipientName }}
                    </div>
                </Tooltip>
                <Tooltip class="payable-receivable-row-tooltip" :title="item.description">
                    <div class="payable-receivable-row-description">{{ item.description }}</div>
                </Tooltip>
            </div>
        </div>

        <div class="payable-receivable-row-center">
            <div class="payable-receivable-row-duedate">{{ item.dueDate }}</div>
            <DotLabel :title="item.status" :color="typeStatus" :icon="iconStatus" size="large" />
        </div>

        <Tooltip :class="['payable-receivable-row-tooltip', { 'payable-receivable-row-tags-expanded': !hasDocuments }]"
            :title="tagsPlainText">
            <div class="payable-receivable-row-tags">
                <Badge v-for="(tag, index) in tagItems" :key="`tag-${item.id}-${index}`" :title="tag" type="default"
                    size="small" />
            </div>
        </Tooltip>

        <Tooltip v-if="hasDocuments" class="payable-receivable-row-tooltip" :title="documentsPlainText">
            <div class="payable-receivable-row-documents">
                <i class="fa-solid fa-file-invoice payable-receivable-row-document-icon"></i>
            </div>
        </Tooltip>
        <div class="payable-receivable-row-bank">
            <img v-if="bankLogoSrc !== ''" class=" payable-receivable-row-bank-image" :src="bankLogoSrc" alt="Banco" />
        </div>

        <div class="payable-receivable-row-value">
            <div class="payable-receivable-row-amount" :class="item.type">
                {{ item.value | currency }}
            </div>
            <div v-if="showBalance" class="payable-receivable-row-balance" :class="balanceClass">
                {{ item.balance | currency }}
            </div>
        </div>

    </div>
</template>

<script>
import Tooltip from "@/ui/layout/Tooltip";

import { mapMutations, mapState } from "vuex";
import Badge from "@/ui/layout/Badge.vue";
import DotLabel from "@/ui/layout/DotLabel.vue";

export default {
    name: "PayableReceivableRow",
    components: { Tooltip, Badge, DotLabel },
    props: {
        item: { type: Object, required: true },
        showBalance: { type: Boolean, default: true },
    },
    computed: {
        ...mapState("generic", ["selected"]),
        selectedIds: {
            get() {
                return this.selected;
            },
            set(value) {
                this.addSelected(value);
            },
        },
        tagItems() {
            return this.normalizeContentItems(this.item.tags);
        },
        documentItems() {
            return this.normalizeContentItems(this.item.documents);
        },
        tagsPlainText() {
            return this.tagItems.join(', ');
        },
        documentsPlainText() {
            return this.documentItems.join(', ');
        },
        labelType() {
            return this.item.type === "receivable" ? "Receber" : "Pagar";
        },
        typeStatus() {
            switch (this.item.status) {
                case "Pago":
                    return "success";
                case "Recebido":
                    return "success";
                case "Recebido Parcial":
                    return "success";
                case "Pago Parcial":
                    return "success";
                case "Pendente":
                    return "danger";
                case "Agendada":
                    return "var(--color-pr-scheduled)";
                case "Hoje":
                    return "var(--color-pr-today)";
                default:
                    return "danger";
            }
        },
        iconStatus() {
            switch (this.item.status) {
                case "Pago":
                    return "fa-solid fa-check-double";
                case "Recebido":
                    return "fa-solid fa-check-double";
                default:
                    return "fa-solid fa-circle";
            }
        },
        hasDocuments() {
            return this.documentItems.length > 0;
        },
        bankLogoSrc() {
            if (!this.item.bankLogo) return '';

            const trimmedBankLogo = this.item.bankLogo.trim();
            const isBankLogoPlaceholder = trimmedBankLogo === "https://espaco.blob.core.windows.net/nixloc-bank-logo/" ||
                trimmedBankLogo === "https://espaco.blob.core.windows.net/nixloc-bank-logo";

            return isBankLogoPlaceholder ? '' : this.item.bankLogo;
        },
        rowClass() {
            const s = this.item.status || "";
            return {
                "payable-receivable-row--late": s.includes("finance-delay"),
            };
        },
        balanceClass() {
            const value = this.normalizeNumericValue(this.item.balance);
            if (value < 0) return "payable-receivable-row-balance-negative";
            if (value > 0) return "payable-receivable-row-balance-positive";
            return "";
        },
    },
    methods: {
        ...mapMutations("generic", ["addSelected"]),
        normalizeNumericValue(value) {
            if (value === null || value === undefined || value === "") return 0;
            if (typeof value === "number") return value;

            const normalized = String(value)
                .replace(/\./g, "")
                .replace(",", ".")
                .replace(/[^0-9.-]/g, "");

            const parsedValue = Number(normalized);
            return Number.isNaN(parsedValue) ? 0 : parsedValue;
        },
        normalizeContentItems(value) {
            if (!Array.isArray(value)) return [];
            return value
                .map((entry) => this.normalizeSingleContentItem(entry))
                .filter(Boolean);
        },
        normalizeSingleContentItem(value) {
            if (value === null || value === undefined) return "";
            return String(value).trim();
        },
    },

};

</script>

<style scoped>
.payable-receivable-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) 150px 300px 80px 40px 130px;
    align-items: center;
    gap: 15px;
    padding: 10px 0;
    border-top: 1px solid var(--border-subtle);
    box-sizing: border-box;
    transition: background 0.15s;
}

.payable-receivable-row-left {
    display: flex;
    align-items: center;
    gap: 15px;
    min-width: 0;
}

.payable-receivable-row-checkbox {
    flex-shrink: 0;
}

.payable-receivable-row-icon {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    color: var(--text-inverse);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    flex-shrink: 0;
}

.payable-receivable-row-badge-wrap {
    flex-shrink: 0;
    width: 80px;
}

.payable-receivable-row-badge-wrap /deep/ .side-by-side {
    display: block;
    width: 100%;
}

.payable-receivable-row-badge-wrap /deep/ .badge {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-left: 0;
}

.payable-receivable-row-info {
    flex: 1 1 0;
    min-width: 0;
}

.payable-receivable-row-recipient {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
    transition: color 0.15s;
}

.payable-receivable-row-recipient:hover {
    color: var(--color-brand);
}

.payable-receivable-row-description {
    font-size: 12px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.payable-receivable-row-center {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.payable-receivable-row-duedate {
    font-size: 13px;
    color: var(--text-secondary);
}

.payable-receivable-row-status {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
}

.payable-receivable-row-tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
}

.payable-receivable-row-tags /deep/ .side-by-side {
    max-width: 100%;
}

.payable-receivable-row-tags /deep/ .badge {
    margin-left: 0;
    max-width: 400px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.payable-receivable-row-documents {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
}

.payable-receivable-row-document-icon {
    color: var(--color-brand);
    font-size: 18px;
}

.payable-receivable-row-bank {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    flex: 0 0 40px;
}

.payable-receivable-row-bank-image {
    width: 32px;
    height: 32px;
    border-radius: 50%;
}

.payable-receivable-row-value {
    text-align: right;
    white-space: nowrap;
}

.payable-receivable-row-amount {
    font-size: 15px;
    font-weight: 700;
}

.payable-receivable-row-amount.payable {
    color: var(--color-danger);
}

.payable-receivable-row-amount.receivable {
    color: var(--color-success);
}

.payable-receivable-row-balance {
    font-size: 12px;
}

.payable-receivable-row-balance.payable {
    color: var(--color-danger);
}

.payable-receivable-row-balance.receivable {
    color: var(--color-success);
}

.payable-receivable-row-balance-negative {
    color: var(--color-danger);
}

.payable-receivable-row-balance-positive {
    color: var(--color-success);
}

.payable-receivable-row-tooltip {
    display: block;
    min-width: 0;
}

.payable-receivable-row-tags-expanded {
    grid-column: span 2;
}

@media (max-width: 1024px) {
    .payable-receivable-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
        padding: 12px 0;
    }

    .payable-receivable-row-left {
        flex: 1 1 100%;
    }

    .payable-receivable-row-center {
        flex-direction: row;
        align-items: center;
        gap: 10px;
    }

    .payable-receivable-row-tooltip {
        flex: 1 1 auto;
    }

    .payable-receivable-row-bank {
        flex: 0 0 auto;
    }

    .payable-receivable-row-value {
        flex: 0 0 auto;
        margin-left: auto;
    }

    .payable-receivable-row-tags-expanded {
        grid-column: unset;
    }
}
</style>